import React, { useRef, useEffect, useState } from 'react';
import {
  AlertTriangle,
  MapPin,
  FileText,
  CheckCircle2,
  Check,
  Shield,
  Image as ImageIcon,
  Phone,
  Send,
  ClipboardList,
  X,
} from 'lucide-react';
import { sendResponsePartnerDispatchService } from '../../utils/responsePartnerService';

function formatDiscoveredAt(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const JobOrderReviewModal = ({ isOpen, onClose, jobOrder, onSent }) => {
  const modalRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setIsSending(false);
      setIsSent(false);
      setSendError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleSend = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!jobOrder?.maintenanceTicketId) return;

    setIsSending(true);
    setSendError(null);

    try {
      await sendResponsePartnerDispatchService(jobOrder.maintenanceTicketId, {
        partnerId: jobOrder.partnerId,
        routingRuleId: jobOrder.routingRuleId,
      });
      setIsSent(true);
      onSent?.();
      window.setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send job order. Please try again.';
      setSendError(message);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen || !jobOrder) return null;

  const stopContentMouseDown = (e) => {
    e.stopPropagation();
  };

  const discoveredFormatted = formatDiscoveredAt(jobOrder.discoveredAt);
  const actionsTaken =
    Array.isArray(jobOrder.actionsTaken) && jobOrder.actionsTaken.length > 0
      ? jobOrder.actionsTaken
      : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="job-order-title"
        onMouseDown={stopContentMouseDown}
        className="bg-white sm:max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col rounded-xl shadow-2xl border border-gray-200/80"
      >
        <div className="p-6 pb-4 border-b border-gray-200 bg-gray-50/90 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-primary-100 shrink-0">
                <ClipboardList className="size-5 text-primary-800" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h2 id="job-order-title" className="text-lg font-semibold text-gray-900">
                  Draft Job Order
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Review before sending to {jobOrder.vendorName}
                </p>
              </div>
            </div>
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 rounded-full p-1.5 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="size-4 text-red-600" strokeWidth={2} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Event Type &amp; Urgency
              </span>
            </div>
            <div className="p-3 bg-red-50 border border-red-200/80 rounded-lg">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="inline-flex text-xs font-semibold px-2 py-1 rounded-md bg-red-600 text-white">
                  {jobOrder.urgencyLevel}
                </span>
                <span className="font-medium text-gray-900">{jobOrder.eventType}</span>
              </div>
              <p className="text-sm text-gray-600">{jobOrder.eventDescription}</p>
              {discoveredFormatted && (
                <p className="text-xs text-gray-500 mt-2">Discovered: {discoveredFormatted}</p>
              )}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="size-4 text-gray-500" strokeWidth={2} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Exact Location
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg space-y-1">
              <p className="text-sm font-medium text-gray-900">{jobOrder.address || '—'}</p>
              {jobOrder.building && (
                <p className="text-sm text-gray-600">{jobOrder.building}</p>
              )}
              {jobOrder.locationDetails && (
                <p className="text-sm text-gray-600">{jobOrder.locationDetails}</p>
              )}
              <p className="text-sm text-gray-600">
                Entry window: {jobOrder.entryWindow || 'ASAP'}
              </p>
              {typeof jobOrder.permissionToEnter === 'boolean' && (
                <p className="text-sm text-gray-600">
                  Permission to enter: {jobOrder.permissionToEnter ? 'Yes' : 'No — contact required'}
                </p>
              )}
              {jobOrder.accessInstructions && (
                <p className="text-sm text-gray-600">
                  Access: {jobOrder.accessInstructions}
                </p>
              )}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="size-4 text-gray-500" strokeWidth={2} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Incident Summary
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                {jobOrder.summary}
              </p>
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="size-4 text-gray-500" strokeWidth={2} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Actions Already Taken
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              {actionsTaken ? (
                <ul className="space-y-1">
                  {actionsTaken.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-900">
                      <Check className="size-4 text-green-600 mt-0.5 shrink-0" strokeWidth={2} />
                      {action}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">None reported</p>
              )}
            </div>
          </div>

          {jobOrder.hasMedia && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="size-4 text-gray-500" strokeWidth={2} />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Attachments
                </span>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900">
                  {jobOrder.mediaNote || 'Photos or video available upon request'}
                </p>
              </div>
            </div>
          )}

          {jobOrder.insuranceClaim && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="size-4 text-gray-500" strokeWidth={2} />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Insurance Information
                </span>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900">
                  <span className="text-gray-500">Claim #:</span> {jobOrder.insuranceClaim}
                </p>
              </div>
            </div>
          )}

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="size-4 text-gray-500" strokeWidth={2} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Preferred Contact for Updates
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {jobOrder.contact?.name || 'Not provided'}
              </p>
              {jobOrder.contact?.phone && (
                <p className="text-sm text-gray-600">{jobOrder.contact.phone}</p>
              )}
              {jobOrder.contact?.email && (
                <p className="text-sm text-gray-600">{jobOrder.contact.email}</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
          <p className="text-xs text-gray-500">
            This job order will be emailed to {jobOrder.vendorName}
            {jobOrder.recipientEmails?.length > 0 && (
              <span className="block mt-0.5 truncate">
                ({jobOrder.recipientEmails.join(', ')})
              </span>
            )}
          </p>
          <div className="flex flex-col items-stretch sm:items-end gap-2">
            {sendError && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1 max-w-xs text-right">
                {sendError}
              </p>
            )}
            <div className="flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isSending || isSent}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending || isSent}
                className={`inline-flex items-center justify-center gap-2 min-w-[140px] px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-80 ${
                  isSent ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSent ? (
                  <>
                    <CheckCircle2 className="size-4" strokeWidth={2} />
                    Sent!
                  </>
                ) : isSending ? (
                  <>
                    <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="size-4" strokeWidth={2} />
                    Send Job Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobOrderReviewModal;
